// ServiceDetail.jsx
import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft, FaCheckCircle, FaRocket } from "react-icons/fa";
import {
  FaCode,
  FaBrain,
  FaMobile,
  FaDesktop,
  FaPalette,
  FaChartLine,
  FaUsers,
} from "react-icons/fa";

const ServiceDetail = ({ serviceId }) => {
  const navigate = useNavigate();

  // Complete services data
  const servicesData = {
    "software-development": {
      id: "software-development",
      icon: FaCode,
      title: "Software Development",
      subtitle: "Web, Mobile, Desktop",
      description:
        "Build powerful web applications, mobile apps, and desktop software tailored to your business needs. We leverage modern technologies to create scalable, performant solutions.",
      detailedDescription:
        "Our software development services cover the entire development lifecycle, from initial concept and design to deployment and maintenance. We specialize in creating custom solutions that address your unique business challenges and help you achieve your goals.",
      features: [
        "Custom Web Applications",
        "Mobile App Development (iOS & Android)",
        "Desktop Application Development",
        "Progressive Web Apps (PWA)",
        "API Development & Integration",
        "Cloud-Native Solutions",
        "Microservices Architecture",
        "Real-time Applications",
      ],
      benefits: [
        {
          title: "Scalability",
          description: "Built to grow with your business needs",
        },
        {
          title: "Performance",
          description: "Optimized for speed and efficiency",
        },
        {
          title: "Security",
          description: "Industry-standard security practices",
        },
        {
          title: "Maintenance",
          description: "Ongoing support and updates",
        },
      ],
      technologies: [
        "React.js",
        "Node.js",
        "Python",
        "Java",
        "C#",
        ".NET",
        "MongoDB",
        "PostgreSQL",
        "AWS",
        "Azure",
        "Docker",
        "Kubernetes",
      ],
      process: [
        {
          title: "Discovery",
          description: "Understanding your requirements and goals",
        },
        {
          title: "Design",
          description: "Creating wireframes and architecture",
        },
        {
          title: "Development",
          description: "Building your solution with best practices",
        },
        {
          title: "Deployment",
          description: "Launch and ongoing support",
        },
      ],
      gradient: "from-blue-500 to-cyan-500",
      heroImage:
        "https://images.unsplash.com/photo-1555066931-4365d14bab8c?auto=format&fit=crop&q=80",
    },
    "ai-ml": {
      id: "ai-ml",
      icon: FaBrain,
      title: "AI & Machine Learning",
      subtitle: "Automation, Insights",
      description:
        "Harness the power of artificial intelligence and machine learning to automate processes, gain insights, and make data-driven decisions.",
      detailedDescription:
        "Transform your business with cutting-edge AI and machine learning solutions. We help you leverage data to create intelligent systems that learn, adapt, and improve over time.",
      features: [
        "Machine Learning Models",
        "Natural Language Processing",
        "Computer Vision Solutions",
        "Predictive Analytics",
        "Intelligent Automation",
        "AI-Powered Chatbots",
        "Deep Learning",
        "Data Mining",
      ],
      benefits: [
        {
          title: "Automation",
          description: "Reduce manual work and increase efficiency",
        },
        {
          title: "Insights",
          description: "Data-driven decision making",
        },
        {
          title: "Prediction",
          description: "Forecast trends and outcomes",
        },
        {
          title: "Personalization",
          description: "Tailored user experiences",
        },
      ],
      technologies: [
        "TensorFlow",
        "PyTorch",
        "Scikit-learn",
        "OpenAI",
        "Hugging Face",
        "Pandas",
        "NumPy",
        "Keras",
        "NLTK",
        "OpenCV",
        "MLflow",
        "Apache Spark",
      ],
      process: [
        {
          title: "Data Collection",
          description: "Gathering and preparing datasets",
        },
        {
          title: "Model Training",
          description: "Building and training AI models",
        },
        {
          title: "Testing",
          description: "Validating model accuracy",
        },
        {
          title: "Integration",
          description: "Deploying into production",
        },
      ],
      gradient: "from-purple-500 to-pink-500",
      heroImage:
        "https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&q=80",
    },
    "ui-ux-design": {
      id: "ui-ux-design",
      icon: FaPalette,
      title: "UI/UX Design",
      subtitle: "User Interface, Brand Identity",
      description:
        "Create stunning, user-friendly interfaces that delight your customers. Our design team crafts experiences that drive engagement and conversion.",
      detailedDescription:
        "We create beautiful, intuitive designs that put your users first. Our design process combines creativity with user research to deliver experiences that are both aesthetically pleasing and highly functional.",
      features: [
        "User Interface Design",
        "User Experience Research",
        "Wireframing & Prototyping",
        "Brand Identity Design",
        "Responsive Design",
        "Design Systems",
        "Usability Testing",
        "Interaction Design",
      ],
      benefits: [
        {
          title: "User Satisfaction",
          description: "Intuitive interfaces users love",
        },
        {
          title: "Brand Consistency",
          description: "Cohesive design across platforms",
        },
        {
          title: "Conversion",
          description: "Optimized for business goals",
        },
        {
          title: "Accessibility",
          description: "Inclusive design for all users",
        },
      ],
      technologies: [
        "Figma",
        "Adobe XD",
        "Sketch",
        "Adobe Illustrator",
        "After Effects",
        "InVision",
        "Framer",
        "Principle",
        "Zeplin",
        "Miro",
        "Photoshop",
        "Webflow",
      ],
      process: [
        {
          title: "Research",
          description: "Understanding user needs",
        },
        {
          title: "Wireframing",
          description: "Creating low-fidelity prototypes",
        },
        {
          title: "Design",
          description: "High-fidelity mockups",
        },
        {
          title: "Testing",
          description: "User feedback and iteration",
        },
      ],
      gradient: "from-pink-500 to-rose-500",
      heroImage:
        "https://images.unsplash.com/photo-1561070791-2526d30994b5?auto=format&fit=crop&q=80",
    },
    "mobile-development": {
      id: "mobile-development",
      icon: FaMobile,
      title: "Mobile Development",
      subtitle: "iOS, Android, Cross-platform",
      description:
        "Native and cross-platform mobile applications that deliver exceptional performance and user experience across all devices.",
      detailedDescription:
        "Build powerful mobile applications that reach users wherever they are. We develop native iOS and Android apps, as well as cross-platform solutions that work seamlessly across devices.",
      features: [
        "Native iOS Development",
        "Native Android Development",
        "React Native Apps",
        "Flutter Development",
        "Mobile App UI/UX",
        "App Store Optimization",
        "Push Notifications",
        "Offline Functionality",
      ],
      benefits: [
        {
          title: "Native Performance",
          description: "Fast, smooth user experience",
        },
        {
          title: "Cross-Platform",
          description: "Single codebase, multiple platforms",
        },
        {
          title: "App Store Ready",
          description: "Optimized for submission",
        },
        {
          title: "Device Integration",
          description: "Camera, GPS, sensors, and more",
        },
      ],
      technologies: [
        "Swift",
        "Kotlin",
        "React Native",
        "Flutter",
        "Dart",
        "Xcode",
        "Android Studio",
        "Firebase",
        "SQLite",
        "Realm",
        "GraphQL",
        "Redux",
      ],
      process: [
        {
          title: "Planning",
          description: "Define features and platform",
        },
        {
          title: "Development",
          description: "Build native or cross-platform",
        },
        {
          title: "Testing",
          description: "QA across devices",
        },
        {
          title: "Launch",
          description: "App store submission",
        },
      ],
      gradient: "from-cyan-500 to-teal-500",
      heroImage:
        "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&q=80",
    },
    erp: {
      id: "erp",
      icon: FaDesktop,
      title: "Enterprise ERP",
      subtitle: "Enterprise, Cloud",
      description:
        "Streamline your business operations with comprehensive ERP solutions. Integrate all aspects of your business into one unified system.",
      detailedDescription:
        "Implement enterprise resource planning systems that unify your business processes. Our ERP solutions help you manage operations, finance, supply chain, and more from a single platform.",
      features: [
        "Custom ERP Development",
        "Cloud-Based ERP",
        "ERP Integration",
        "Business Process Automation",
        "Real-time Analytics",
        "Scalable Architecture",
        "Multi-module Systems",
        "Data Migration",
      ],
      benefits: [
        {
          title: "Integration",
          description: "Unified business processes",
        },
        {
          title: "Efficiency",
          description: "Automated workflows",
        },
        {
          title: "Visibility",
          description: "Real-time business insights",
        },
        {
          title: "Compliance",
          description: "Regulatory adherence",
        },
      ],
      technologies: [
        "SAP",
        "Oracle",
        "Microsoft Dynamics",
        "Odoo",
        "NetSuite",
        "Salesforce",
        "ERPNext",
        "PostgreSQL",
        "Redis",
        "Kafka",
        "RabbitMQ",
        "Elasticsearch",
      ],
      process: [
        {
          title: "Analysis",
          description: "Business requirements gathering",
        },
        {
          title: "Customization",
          description: "Tailoring to your needs",
        },
        {
          title: "Implementation",
          description: "System deployment",
        },
        {
          title: "Training",
          description: "User onboarding and support",
        },
      ],
      gradient: "from-indigo-500 to-purple-500",
      heroImage:
        "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80",
    },
    "digital-marketing": {
      id: "digital-marketing",
      icon: FaChartLine,
      title: "Digital Marketing",
      subtitle: "SEO, Growth",
      description:
        "Grow your online presence with data-driven digital marketing strategies. From SEO to social media, we help you reach your target audience.",
      detailedDescription:
        "Boost your brand visibility and drive meaningful engagement through strategic digital marketing. We create data-driven campaigns that deliver measurable results.",
      features: [
        "Search Engine Optimization",
        "Content Marketing",
        "Social Media Marketing",
        "Pay-Per-Click Advertising",
        "Email Marketing Campaigns",
        "Analytics & Reporting",
        "Conversion Optimization",
        "Influencer Marketing",
      ],
      benefits: [
        {
          title: "Visibility",
          description: "Increased online presence",
        },
        {
          title: "Traffic",
          description: "More qualified visitors",
        },
        {
          title: "Leads",
          description: "Higher conversion rates",
        },
        {
          title: "ROI",
          description: "Measurable business growth",
        },
      ],
      technologies: [
        "Google Analytics",
        "Google Ads",
        "Facebook Ads",
        "SEMrush",
        "Ahrefs",
        "Mailchimp",
        "HubSpot",
        "Hootsuite",
        "Buffer",
        "Moz",
        "Hotjar",
        "Tableau",
      ],
      process: [
        {
          title: "Strategy",
          description: "Define goals and audience",
        },
        {
          title: "Campaign",
          description: "Create and launch campaigns",
        },
        {
          title: "Monitor",
          description: "Track performance metrics",
        },
        {
          title: "Optimize",
          description: "Continuous improvement",
        },
      ],
      gradient: "from-orange-500 to-red-500",
      heroImage:
        "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80",
    },
    testing: {
      id: "testing",
      icon: FaCheckCircle,
      title: "End to End Testing",
      subtitle: "QA, Security",
      description:
        "Ensure quality and security with comprehensive testing services. We identify and fix issues before they reach your users.",
      detailedDescription:
        "Deliver flawless software with our comprehensive testing services. We ensure your applications are bug-free, secure, and perform optimally under all conditions.",
      features: [
        "Automated Testing",
        "Manual Testing",
        "Performance Testing",
        "Security Testing",
        "User Acceptance Testing",
        "Continuous Integration Testing",
        "Load Testing",
        "Regression Testing",
      ],
      benefits: [
        {
          title: "Quality",
          description: "Bug-free software delivery",
        },
        {
          title: "Security",
          description: "Vulnerability detection",
        },
        {
          title: "Performance",
          description: "Optimized under load",
        },
        {
          title: "Confidence",
          description: "Peace of mind for deployment",
        },
      ],
      technologies: [
        "Selenium",
        "Jest",
        "Cypress",
        "JUnit",
        "Mocha",
        "Postman",
        "JMeter",
        "Burp Suite",
        "TestRail",
        "Appium",
        "SonarQube",
        "OWASP ZAP",
      ],
      process: [
        {
          title: "Planning",
          description: "Define test strategy",
        },
        {
          title: "Execution",
          description: "Run test cases",
        },
        {
          title: "Reporting",
          description: "Document findings",
        },
        {
          title: "Verification",
          description: "Confirm fixes",
        },
      ],
      gradient: "from-green-500 to-emerald-500",
      heroImage:
        "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&q=80",
    },
    staffing: {
      id: "staffing",
      icon: FaUsers,
      title: "Contract Staffing",
      subtitle: "Expert Resources",
      description:
        "Access skilled professionals on-demand. We provide talented developers, designers, and specialists to augment your team.",
      detailedDescription:
        "Scale your team quickly with top-tier tech talent. Our contract staffing services provide you with skilled professionals who integrate seamlessly into your existing workflows.",
      features: [
        "Full-Stack Developers",
        "Frontend Specialists",
        "Backend Engineers",
        "DevOps Engineers",
        "UI/UX Designers",
        "Project Managers",
        "QA Engineers",
        "Data Scientists",
      ],
      benefits: [
        {
          title: "Flexibility",
          description: "Scale team as needed",
        },
        {
          title: "Expertise",
          description: "Skilled professionals",
        },
        {
          title: "Cost-Effective",
          description: "No long-term commitments",
        },
        {
          title: "Quick Onboarding",
          description: "Start within days",
        },
      ],
      technologies: [
        "React",
        "Angular",
        "Vue.js",
        "Node.js",
        "Python",
        "Java",
        "AWS",
        "Azure",
        "GCP",
        "Docker",
        "Kubernetes",
        "CI/CD",
      ],
      process: [
        {
          title: "Requirements",
          description: "Define your needs",
        },
        {
          title: "Matching",
          description: "Find the right talent",
        },
        {
          title: "Onboarding",
          description: "Quick integration",
        },
        {
          title: "Management",
          description: "Ongoing support",
        },
      ],
      gradient: "from-yellow-500 to-orange-500",
      heroImage:
        "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&q=80",
    },
  };

  const service = servicesData[serviceId];

  if (!service) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center pt-32">
        <div className="text-center">
          <p className="text-xl mb-4">Service not found</p>
          <button
            onClick={() => navigate("/services")}
            className="px-6 py-3 bg-cyan-500 rounded-full hover:bg-cyan-600 transition-colors"
          >
            Back to Services
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative bg-black text-white pt-36 pb-24 px-4 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 z-0">
          <img
            src={service.heroImage}
            alt={service.title}
            className="w-full h-full object-cover opacity-70 grayscale"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/50 to-black"></div>
        </div>

        {/* Animated Blobs */}
        <div className="absolute inset-0 overflow-hidden z-0 pointer-events-none">
          <motion.div
            animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
            className={`absolute top-0 left-1/4 w-96 h-96 bg-gradient-to-br ${service.gradient} opacity-30 rounded-full blur-[120px]`}
          />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl">
          {/* Back Button */}
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => navigate("/services")}
            className="flex items-center gap-2 text-white/80 hover:text-white transition-colors mb-8 group"
          >
            <FaArrowLeft className="transition-transform group-hover:-translate-x-1" />
            <span>Back to Services</span>
          </motion.button>

          {/* Title */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div
              className={`inline-flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br ${service.gradient} text-4xl text-white shadow-lg mb-6`}
            >
              <service.icon />
            </div>
            <h1 className="text-5xl md:text-7xl font-extrabold tracking-tighter mb-4 bg-gradient-to-b from-white via-white to-gray-300 bg-clip-text text-transparent drop-shadow-lg">
              {service.title}
            </h1>
            <p className="text-xl text-white/90 max-w-3xl drop-shadow-md">
              {service.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="bg-[#f8fafc] py-24 px-4">
        <div className="mx-auto max-w-7xl">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Left: What We Offer */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                What We Offer
              </h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                {service.detailedDescription}
              </p>
              <ul className="space-y-4">
                {service.features.map((feature, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start gap-3"
                  >
                    <FaCheckCircle
                      className={`mt-1 flex-shrink-0 text-lg bg-gradient-to-br ${service.gradient} bg-clip-text text-transparent`}
                    />
                    <span className="text-gray-700">{feature}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            {/* Right: Benefits */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Key Benefits
              </h2>
              <div className="space-y-6">
                {service.benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white/60 backdrop-blur-md rounded-xl p-6 border border-white/60 shadow-lg"
                  >
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {benefit.title}
                    </h3>
                    <p className="text-gray-600">{benefit.description}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Technologies/Tools Section */}
      <section className="bg-white py-24 px-4">
        <div className="mx-auto max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Technologies & Tools
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We use industry-leading technologies to deliver exceptional
              results
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {service.technologies.map((tech, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ y: -5 }}
                className="bg-gray-50 rounded-xl p-6 text-center border border-gray-200 hover:border-gray-300 transition-all hover:shadow-md"
              >
                <p className="font-semibold text-gray-900">{tech}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="bg-[#f8fafc] py-24 px-4">
        <div className="mx-auto max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Process
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              A streamlined approach to deliver quality results
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {service.process.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative"
              >
                <div className="bg-white/60 backdrop-blur-md rounded-xl p-6 border border-white/60 shadow-lg hover:shadow-xl transition-shadow">
                  <div
                    className={`inline-flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br ${service.gradient} text-white font-bold text-xl mb-4`}
                  >
                    {index + 1}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 text-sm">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-slate-900 via-purple-900 to-slate-900 py-24 px-4 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>

        <div className="relative mx-auto max-w-4xl text-center text-white z-10">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            onClick={() => navigate("/contact")}
            className="mb-6 text-4xl font-extrabold md:text-5xl"
          >
            Ready to Get Started?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="mb-10 text-lg text-blue-100/80"
          >
            Let's discuss how we can help you with {service.title.toLowerCase()}
          </motion.p>
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate("/contact")}
            className="group inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-lg font-bold text-purple-900 shadow-lg"
          >
            <FaRocket />
            Start Your Project
          </motion.button>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
